<template>
  <div
    class="psui-el-tab-header"
    :class="[getComponentClass, `layout-${layout}`]"
    role="tablist"
    :aria-label="ariaLabel"
    @keydown="onKeyDown"
  >
    <template v-for="(item, index) in getItems">
      <slot :item="item">
        <PsRichTooltip
          v-if="item.tooltip"
          :title="item.tooltip"
          layout="blue"
        >
          <template #trigger>
            <button
              :key="item[keyValue] + index"
              ref="tabButtons"
              type="button"
              role="tab"
              :aria-selected="getSelected === item[keyValue]"
              :tabindex="getSelected === item[keyValue] ? 0 : -1"
              :aria-controls="ariaControlsPrefix ? `${ariaControlsPrefix}${item.id || item[keyValue]}` : undefined"
              @click="selectTab(item)"
              :class="getButtonClass(item).value"
            >
              <PsIcon
                v-if="item.icon"
                :icon="item.icon"
                display="flex"
                tabindex="-1"
                aria-hidden="true"
                size="18"
              />
              <span>{{ item[keyLabel] }}</span>
            </button>
          </template>
        </PsRichTooltip>
        <button  
          v-else
          :id="item.id ? item.id : (idSuffix ? `${item[keyValue]}${idSuffix}` : undefined)"       
          :key="item[keyValue] + index"
          ref="tabButtons"
          type="button"
          role="tab"
          :aria-selected="getSelected === item[keyValue]"
          :tabindex="getSelected === item[keyValue] ? 0 : -1"
          :aria-controls="ariaControlsPrefix ? `${ariaControlsPrefix}${item.id || item[keyValue]}` : undefined"
          @click="selectTab(item)"
          :class="getButtonClass(item).value"
        >
          <PsIcon
            v-if="item.icon"
            :icon="item.icon"
            display="flex"
            tabindex="-1"
            aria-hidden="true"
            size="18"
          />
          <span>{{ item[keyLabel] }}</span>
        </button>
      </slot>
    </template>
  </div>
</template>

<script setup>
import PsRichTooltip from '../tooltip/PsRichTooltip.vue'
import { computed, ref } from 'vue'
import PsIcon from '../ui/PsIcon.vue'

const props = defineProps({
  /**
   * It sets the layout of the tabheader. eg: standard, underline and folder.
   */
  layout: {
    type: String,
    default: 'standard',
    validator: (value) => ['standard', 'underline', 'folder', 'policy-design'].includes(value),
  },
  /**
   * It sets the tabs which will be rendered.
   */
  items: {
    type: Array,
    required: true,
  },
  /**
   * It sets the key label of the items if needed.
   */
  keyLabel: {
    type: String,
    default: 'label',
  },
  /**
   * It sets the key value of the items if needed.
   */
  keyValue: {
    type: String,
    default: 'value',
  },
  /**
   * It disabled the tab header. All mouse events are disabled.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * It sets the tab seleced.
   */
  selected: {},
  /**
   * The aria-label for the tablist.
   */
  ariaLabel: {
    type: String,
    default: 'Tabs',
  },
  /**
   * Prefix for the aria-controls attribute.
   */
  ariaControlsPrefix: {
    type: String,
    default: '',
  },
  /**
   * Suffix for the tab ID.
   */
  idSuffix: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['change', 'update:selected'])

const tabButtons = ref([])

const getComponentClass = computed(() => {
  if (props.disabled) {
    return 'status-disabled'
  } 

  return ''
})

const getIsObject = computed(() => {
  return typeof props.selected === 'object'
})

const getSelected = computed(() => {
  if (props.selected) {
    if (typeof props.selected === 'object' && props.selected[props.keyValue]) {
      return props.selected[props.keyValue]
    } else {
      return props.selected
    }
  } else {
    return false
  }
})

const getItems = computed(() => {
  if (props.items.length > 0 && typeof props.items[0] !== 'object') {
    return props.items.map((item) => {
      return {
        [props.keyLabel]: item,
        [props.keyValue]: item,
      }
    })
  } else {
    return props.items
  }
})

const selectTab = (item) => {
  if (item.disabled !== true) {
    emit('update:selected', getIsObject.value ? item : item[props.keyValue])
    emit('change', getIsObject.value ? item : item[props.keyValue])
  }
}

const onKeyDown = (e) => {
  const tabs = tabButtons.value.filter(btn => btn !== null)
  let currentIndex = tabs.findIndex(btn => btn === document.activeElement)

  if (currentIndex === -1) return

  let nextIndex

  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      e.preventDefault()
      nextIndex = (currentIndex + 1) % tabs.length
      break
    case 'ArrowLeft':
    case 'ArrowUp':
      e.preventDefault()
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
      break
    case 'Home':
      e.preventDefault()
      nextIndex = 0
      break
    case 'End':
      e.preventDefault()
      nextIndex = tabs.length - 1
      break
    default:
      return
  }

  if (nextIndex !== undefined) {
    const nextTab = nextIndex >= 0 && nextIndex < getItems.value.length ? getItems.value[nextIndex] : null
    if (nextTab && nextTab.disabled !== true) {
      selectTab(nextTab)
      tabs[nextIndex].focus()
    }
  }
}

const getButtonClass = (item) => {
  return computed(() => {
    if (item.disabled) {
      return ['status-disabled']
    } else {
      return { 'status-active': getSelected.value === item[props.keyValue], 'status-enabled': true }
    }
  })
}

</script>

<style>
/* Please, use the file src/assets/scss/components/PsTabHeader.scss */
</style>
